import React, {
  useEffect,
  useState,
} from "react";

function safeItems(value) {
  return Array.isArray(value)
    ? value
    : [];
}

export default function CreativeProjectPreviewShell({
  lang = "en",
  title = "",
  items = [],
  renderItem,
  onReturn,
  onExit,
  returnLabel = "",
  initialActiveIndex = 0,
  onActiveIndexChange,
  previewLabel = "",
  previousLabel = "",
  nextLabel = "",
  description = "",
}) {
  const isFR =
    String(lang).toLowerCase() === "fr";

  const previewItems =
    safeItems(items);

  const requestedInitialIndex =
    Number(
      initialActiveIndex
    );

  const startingIndex =
    previewItems.length > 0 &&
    Number.isInteger(
      requestedInitialIndex
    )
      ? Math.min(
          previewItems.length - 1,
          Math.max(
            0,
            requestedInitialIndex
          )
        )
      : 0;

  const [
    activeIndex,
    setActiveIndex,
  ] = useState(
    startingIndex
  );

  useEffect(() => {
    setActiveIndex((current) => {
      if (
        previewItems.length === 0
      ) {
        return 0;
      }

      return Math.min(
        current,
        previewItems.length - 1
      );
    });
  }, [previewItems.length]);

  const activeItem =
    previewItems[activeIndex] ||
    null;

  const canGoPrevious =
    activeIndex > 0;

  const canGoNext =
    activeIndex <
    previewItems.length - 1;

  const positionLabel =
    activeItem?.positionLabel ||
    (
      previewItems.length > 0
        ? `${activeIndex + 1} / ${previewItems.length}`
        : (
            isFR
              ? "Aucun contenu"
              : "No content"
          )
    );

  function moveToIndex(
    nextIndex
  ) {
    if (
      previewItems.length === 0
    ) {
      return;
    }

    const boundedIndex =
      Math.min(
        previewItems.length - 1,
        Math.max(
          0,
          nextIndex
        )
      );

    setActiveIndex(
      boundedIndex
    );

    if (
      typeof onActiveIndexChange ===
        "function"
    ) {
      onActiveIndexChange(
        boundedIndex,
        previewItems[
          boundedIndex
        ] || null
      );
    }
  }

  return (
    <section
      aria-label={
        isFR
          ? "Aperçu du projet créatif"
          : "Creative project preview"
      }
    >
      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              padding: "6px 10px",
              borderRadius: 999,
              background: "#ffedd5",
              color: "#9a3412",
              fontSize: 12,
              fontWeight: 900,
              letterSpacing:
                "0.08em",
              textTransform:
                "uppercase",
            }}
          >
            {previewLabel ||
              (
                isFR
                  ? "Aperçu brouillon"
                  : "Draft Preview"
              )}
          </div>

          <h2
            className="ns-section-title"
            style={{
              margin: "10px 0 6px",
            }}
          >
            {title ||
              (
                isFR
                  ? "Projet sans titre"
                  : "Untitled Project"
              )}
          </h2>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              maxWidth: 720,
              lineHeight: 1.55,
            }}
          >
            {description ||
              (
                isFR
                  ? "Cette vue privée permet de parcourir le projet tel qu’un lecteur pourrait le découvrir. Elle ne publie et n’exporte rien."
                  : "This private view lets you experience the project as a reader might. It does not publish or export anything."
              )}
          </p>
        </div>

        {typeof onExit ===
        "function" ? (
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={onExit}
          >
            {isFR
              ? "Retour à Mes projets"
              : "Return to My Projects"}
          </button>
        ) : null}
      </div>

      <div
        className="ns-card"
        style={{
          padding: 14,
          marginBottom: 16,
          borderRadius: 16,
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={!canGoPrevious}
          onClick={() =>
            moveToIndex(
              activeIndex - 1
            )
          }
        >
          {previousLabel ||
            (
              isFR
                ? "Précédent"
                : "Previous"
            )}
        </button>

        <strong
          aria-live="polite"
          style={{
            color: "#334155",
          }}
        >
          {positionLabel}
        </strong>

        <button
          className="ns-button ns-button-primary"
          type="button"
          disabled={!canGoNext}
          onClick={() =>
            moveToIndex(
              activeIndex + 1
            )
          }
        >
          {nextLabel ||
            (
              isFR
                ? "Suivant"
                : "Next"
            )}
        </button>
      </div>

      <div
        className="ns-card ns-nbb-major-panel"
        style={{
          padding:
            "clamp(18px, 3vw, 30px)",
          borderRadius: 20,
          minHeight: 360,
          background:
            "linear-gradient(145deg, #ffffff, #fffaf5)",
        }}
      >
        {activeItem &&
        typeof renderItem ===
          "function" ? (
          renderItem(
            activeItem,
            activeIndex
          )
        ) : (
          <div
            style={{
              minHeight: 300,
              display: "grid",
              placeItems: "center",
              textAlign: "center",
            }}
          >
            <div>
              <strong>
                {isFR
                  ? "Aucun contenu à prévisualiser"
                  : "Nothing to preview yet"}
              </strong>

              <p
                className="ns-card-meta"
                style={{
                  margin:
                    "8px 0 0",
                }}
              >
                {isFR
                  ? "Ajoutez du contenu dans l’espace de travail du projet."
                  : "Add content in the project workspace."}
              </p>
            </div>
          </div>
        )}
      </div>

      {typeof onReturn ===
      "function" ? (
        <div
          style={{
            marginTop: 18,
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={onReturn}
          >
            {returnLabel ||
              (
                isFR
                  ? "Retour"
                  : "Return"
              )}
          </button>
        </div>
      ) : null}
    </section>
  );
}
